import { SpinnerService } from '@app/services/spinner.service';
import { PaymentScheduleApi } from '@app/services/apiServices/PaymentScheduleApi';
import { PaymentSchedule } from '@app/models/PaymentSchedule';
import { ErrorHandlerService } from '@app/services/ErrorHandler.service';
import { PropertyApi } from '@app/services/apiServices/PropertyApi';
import { Property } from '@app/models/Property';
import { NetworkErrorService } from "@app/services/NetworkError.service";
import { take } from "rxjs/operators";
import { Component, Inject, OnInit } from "@angular/core";
import { FuturePayment } from "@app/models/FuturePayment";
import { MatDialog } from "@angular/material/dialog";
import { WindowToken } from "@app/util/window.token";
import { ScheduleViewCancelDialog } from "@app/pages/payment/scheduleView/scheduleViewCancel.dialog";
import { OneButtonDialogComponent } from "@app/components/sharedDialogs/oneButtonDialog.component";
import { ScheduleViewRestoreDialog } from "@app/pages/payment/scheduleView/scheduleViewRestore.dialog";
import { ScheduleViewDeleteDialog } from "@app/pages/payment/scheduleView/scheduleViewDelete.dialog";
import { PaymentDateHelperDialog } from "@app/pages/payment/scheduleView/paymentDateHelper.dialog";
import { ActivatedRoute, Router } from "@angular/router";

@Component({
    selector: 'app-schedule-view',
    template: require('./scheduleView.component.html')
})
export class ScheduleViewComponent implements OnInit {

    paymentScheduleId: number;
    paymentSchedule: PaymentSchedule;
    property: Property;
    doneLoading: boolean;
    networkErrorService: NetworkErrorService
    sortedFuturePayments: FuturePayment[];

    constructor(@Inject(MatDialog) private dialog: MatDialog,
                @Inject(SpinnerService) private spinnerService: SpinnerService,
                @Inject(PaymentScheduleApi) private paymentScheduleApi: PaymentScheduleApi,
                @Inject(ErrorHandlerService) private errorHandlerService: ErrorHandlerService,
                @Inject(PropertyApi) private propertyApi: PropertyApi,
                @Inject(WindowToken) private $window,
                @Inject(Router) private router: Router,
                @Inject(ActivatedRoute) private route: ActivatedRoute) {


        if (this.route.snapshot.params.scheduleId) {
            this.paymentScheduleId = this.route.snapshot.params.scheduleId;
        } else if (!this.$window.__karma__) {
            console.error("No paymentScheduleId");
        }

    }

    ngOnInit() {
        this.spinnerService.showSpinner();
        this.doneLoading = false;
        this.getPaymentSchedule().then(() => {
            this.doneLoading = true;
            this.spinnerService.closeSpinner();
        })
    }

    getPaymentSchedule(): Promise<void> {

        return new Promise<void>((resolve, reject) => {
            this.paymentScheduleApi.paymentSchedulesIdGet(this.paymentScheduleId)
                .pipe(take(1)).subscribe({ next: resp => {
                    this.paymentSchedule = resp;
                    this.createSortedFuturePayments();
                    this.propertyApi.propertiesIdGet(this.paymentSchedule.propertyId)
                        .pipe(take(1)).subscribe({ next: resp => {
                            this.property = resp;
                            resolve();
                        }, error: () => {
                            reject();
                        } });
                }, error: () => {
                    reject();
                } });
        });
    }

    cancel() {
        this.router.navigateByUrl('/properties');
    }

    cancelPayment(futurePayment) {
        let fp = JSON.parse((JSON.stringify(futurePayment)));
        let dialogRef = this.dialog.open(ScheduleViewCancelDialog, {
            data: {
                paymentDate: fp.date.formattedFullDate,
                paymentAmount: fp.total
            }
        })

        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value === "Cancel") {
                fp.status = 'cancelled-by-user';
                this.spinnerService.showSpinner();
                this.paymentScheduleApi.futurePaymentsPost(fp)
                    .pipe(take(1)).subscribe({ next: () => {
                        console.debug('changed future payment ' + fp.id + ' status to cancelled');
                        this.getPaymentSchedule().then(() => {
                            this.spinnerService.closeSpinner();
                        });
                    }, error: (err) => {
                        console.debug('error changing future payment ' + fp.id + ' status to cancelled');
                        if (err && err.status === 400) {
                            this.dialog.open(OneButtonDialogComponent, {
                                data: {
                                    header: 'Cancel Payment',
                                    message: err.error.message,
                                    buttonText: 'OK'
                                }
                            })
                        } else {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe(() => {
                                    // nothing to do
                                }, () => {});
                        }
                        this.getPaymentSchedule().then(() => {
                            this.spinnerService.closeSpinner();
                        });
                    } });
            }
        }, () => {});
    }

    restorePayment(futurePayment) {
        let fp = JSON.parse((JSON.stringify(futurePayment)));
        let dialogRef = this.dialog.open(ScheduleViewRestoreDialog, {
            data: {
                paymentDate: fp.date.formattedFullDate,
                paymentAmount: fp.total
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value === "Restore") {
                fp.status = 'scheduled';
                this.spinnerService.showSpinner();
                this.paymentScheduleApi.futurePaymentsPost(fp)
                    .pipe(take(1)).subscribe({ next: () => {
                        console.debug('changed future payment ' + fp.id + ' status to scheduled');
                        this.getPaymentSchedule().then(() => {
                            this.spinnerService.closeSpinner();
                        });
                    }, error: (err) => {
                        console.debug('error changing future payment ' + fp.id + ' status to scheduled');
                        if (err && err.status === 400) {
                            this.dialog.open(OneButtonDialogComponent, {
                                data: {
                                    header: 'Restore Payment',
                                    message: err.error.message,
                                    buttonText: 'OK'
                                }
                            });
                        } else {
                            this.networkErrorService.handleNetworkError(err, true)
                                .pipe(take(1)).subscribe({ next: () => {}, error: () => {} });
                        }
                        this.getPaymentSchedule().then(() => {
                            this.spinnerService.closeSpinner();
                        });
                    } })
            }
        });
    }

    removeSchedule() {
        let dialogRef = this.dialog.open(ScheduleViewDeleteDialog, {
            data: {
                // accessed via ngDialogData.nickname in the template
                nickname: this.paymentSchedule && this.paymentSchedule.nickname
            }
        });

        dialogRef.afterClosed().pipe(take(1)).subscribe(value => {
            if (value === "Delete") {
                this.spinnerService.showSpinner();
                this.paymentScheduleApi.paymentSchedulesIdDelete(this.paymentSchedule.id)
                    .pipe(take(1)).subscribe({ next: () => {
                        this.spinnerService.closeSpinner();
                        this.router.navigateByUrl('/properties');
                    }, error: (err) => {
                        let errMsg: string = undefined;
                        if (err.error && err.error.message) {
                            errMsg = err.error.message;
                        }
                        this.errorHandlerService.handleNetworkError(err, true, errMsg)
                            .pipe(take(1)).subscribe({ next: () => {}, error: () => {} });
                    } })
            }
        })

    }

    /**
     * Open tooltip dialog for payment date edit.
     */
    paymentDatedHelper() {
        this.dialog.open(PaymentDateHelperDialog);
    }

    createSortedFuturePayments() {
        this.sortedFuturePayments = [];

        if (this.paymentSchedule &&
            this.paymentSchedule.futurePayments &&
            this.paymentSchedule.futurePayments.length > 0) {

            this.sortedFuturePayments = this.paymentSchedule.futurePayments.sort((a, b) => {

                let yearResult = a.date.year - b.date.year;
                if (yearResult !== 0) {
                    return yearResult;
                }

                let monthResult = a.date.month - b.date.month;
                if (monthResult !== 0) {
                    return monthResult;
                }

                let dayResult = a.date.day - b.date.day;
                if (dayResult !== 0) {
                    return dayResult;
                }

                return a.id - b.id;
            });

        }

    }
}

